import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { FC, PropsWithChildren } from "react";
import { cva, type VariantProps } from "class-variance-authority";

const pageContentVariants = cva("flex grow flex-col", {
    variants: {
        padding: {
            default: "p-6",
            none: "",
        },
        scrollable: {
            // The margin ensures that there's a tiny gap between the scrollbar and the edges
            // of the content. Larger at the bottom due to rounded corners.
            true: "mx-[1px] mb-[3px] mt-[1px]",
            // If the content isn't scrollable, we set overflow-hidden so the children can manage
            // their own scroll state.
            false: "overflow-hidden",
        },
    },
    defaultVariants: {
        padding: "default",
        scrollable: true,
    },
});

type PageContentProps = PropsWithChildren &
    PropsWithClassName &
    VariantProps<typeof pageContentVariants> & {
        scrollable?: boolean;
    };

export const PageContent: FC<PageContentProps> = ({ children, className, padding, scrollable = true }) => {
    const classes = cn(pageContentVariants({ padding, scrollable }), className);

    if (scrollable) {
        return (
            <ScrollArea orientation="vertical" className={classes}>
                {children}
            </ScrollArea>
        );
    }

    return <div className={classes}>{children}</div>;
};
